<template>
  <b-overlay :show="loading" class="animated">
    <v-server-table
      :columns="table_config.columns"
      :options="table_config.options"
      :theme="theme"
      :ref="ref"
      id="instrument-instance-schedule-server-table"
      class="w-100"
    >
      <span slot="process_schedule.schedule.date" slot-scope="{ row }">
        <existence-gate :entity="row" :levels="table_data.date" :field="'date'">
          <template #existing-content>
            <router-link
              :to="`/schedules/${getData(row, {
                levels: table_data.date,
                field: 'id',
              })}`"
            >
              {{ getData(row, { levels: table_data.date, field: "date" }) }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="instrument_instance.instrument.name" slot-scope="{ row }">
        <existence-gate
          :entity="row"
          :levels="table_data.instrument"
          :field="'name'"
        >
          <template #existing-content>
            <router-link
              :to="`/instrument/${getData(row, {
                levels: table_data.instrument,
                field: 'id',
              })}`"
            >
              {{
                getData(row, { levels: table_data.instrument, field: "name" })
              }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="instrument_instance.name" slot-scope="{ row }">
        <existence-gate
          :entity="row"
          :levels="['instrument_instance']"
          :field="'name'"
        >
          <template #existing-content>
            <router-link
              :to="`/instrument-instances/${getData(row, {
                levels: ['instrument_instance'],
                field: 'id',
              })}`"
            >
              {{ getData(row, { levels: ['instrument_instance'], field: "name" }) }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span
        slot="process_schedule.schedulable_process.name"
        slot-scope="{ row }"
      >
        <existence-gate
          :entity="row"
          :levels="table_data.process"
          :field="'name'"
        >
          <template #existing-content>
            <router-link
              :to="`/${process_type}-processes/${getData(row, {
                levels: table_data.process,
                field: 'id',
              })}`"
            >
              {{ getData(row, { levels: table_data.process, field: "name" }) }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="schedulable_task.name" slot-scope="{ row }">
        <existence-gate :entity="row" :levels="['schedulable_task']" :field="'name'">
          <template #existing-content>
            <router-link
              :to="`/${process_type}-tasks/${getData(row, {
                levels: ['schedulable_task'],
                field: 'id',
              })}`"
            >
              {{ getData(row, { levels: ['schedulable_task'], field: "name" }) }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="status" slot-scope="{ row }">
        <existence-gate
          :entity="row"
          :levels="table_data.status"
          :field="'status'"
        >
          <template #existing-content>
            <b-badge
              v-if="
                getData(row, { levels: table_data.status, field: 'status' }) ===
                'Draft'
              "
              variant="warning"
              pill
              >Draft</b-badge
            >
            <b-badge
              v-if="
                getData(row, { levels: table_data.status, field: 'status' }) ===
                'Declined'
              "
              variant="danger"
              pill
              >Declined</b-badge
            >
            <b-badge
              v-if="
                getData(row, { levels: table_data.status, field: 'status' }) ===
                'Approved'
              "
              variant="success"
              pill
              >Approved</b-badge
            >
          </template>
        </existence-gate>
      </span>
    </v-server-table>
  </b-overlay>
</template>

<script>
import Vue from "vue";
import BaseServerTable from "@/components/shared/BaseServerTable";
import ExistenceGate from "@/components/shared/ExistenceGate";
import InstrumentInstanceScheduleCollection from "@/collections/lab/InstrumentInstanceScheduleCollection";
import { ServerTable } from "vue-tables-2";
import { getEntityData } from "@/shared/utils";

Vue.use(ServerTable);

export default {
  name: "InstrumentInstanceScheduleServerTable",
  extends: BaseServerTable,
  components: {
    ServerTable,
    ExistenceGate,
  },
  props: {
    collection: {
      type: Object,
      default: () => new InstrumentInstanceScheduleCollection(),
    },
    task_id_collection: {
      type: Array,
      default: () => null,
    },
    process_type: {
      type: String,
      default: null,
    },
    show_columns: {
      type: Array,
      default: () => [
        "process_schedule.schedule.date",
        "instrument_instance.instrument.name",
        "instrument_instance.name",
        "process_schedule.schedulable_process.name",
        "schedulable_task.name",
        "status",
      ],
    },
    filterable: {
      type: Array,
      default: () => ["searchable"],
    },
    sortable: {
      type: Array,
      default: () => [
        "process_schedule.schedule.date",
        "instrument_instance.instrument.name",
        "instrument_instance.name",
        "process_schedule.schedulable_process.name",
      ],
    },
  },
  data() {
    return {
      sample_id: this.$route.params.sample_id || null,
      table_config: this.lodash.merge(this.getDefaultTableConfig(), {
        options: {
          headings: {
            "process_schedule.schedule.date": "Schedule Date",
            "instrument_instance.instrument.name": "Instrument",
            "instrument_instance.name": "Instance",
            "process_schedule.schedulable_process.name": "Process",
            status: "Schedule Status",
          },
          filterable: this.filterable,
          sortable: this.sortable,
        },
      }),
      table_data: {
        date: ["process_schedule", "schedule"],
        instrument: ["instrument_instance", "instrument"],
        process: ["process_schedule", "schedulable_process"],
        status: [
          "process_schedule",
          "schedule",
          "current_schedule_status_log",
          "schedule_status",
        ],
      },
    };
  },
  methods: {
    getData({ _reference }, data) {
      return getEntityData({
        entity: _reference,
        ...data,
      });
    },
    prepareCollectionFilter(filter) {
      const connections = [
        "instrument_instance.instrument",
        "schedulable_task",
        "process_schedule.schedule.current_schedule_status_log.schedule_status",
        "process_schedule.schedulable_process",
        `schedulable_task.${this.process_type}_process`,
      ];
      connections.forEach((connection) => {
        filter.connectionAdd(connection);
      });

      this.sample_id && filter.filterEquals("sample_id", this.sample_id);
      this.task_id_collection &&
        filter.filterIn("schedulable_task_id", this.task_id_collection);

      return filter;
    },
  },
};
</script>
